import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Bot, Save, Mail, Briefcase, MessageSquare, Brain, Settings2 } from 'lucide-react';
import api from '../../lib/api';

const AI_MODELS = [
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Consigliato - Bilanciato)' },
  { value: 'gpt-4o', label: 'GPT-4o (Più intelligente, più costoso)' },
  { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini (Economico)' },
];

export const ChatbotAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    enabled: true,
    ai_model: 'gpt-4o-mini',
    notification_email: 'sales@startupitalia.eu',
    monday_board_id: '',
    monday_group_id: '',
    monday_columns: {
      email: '',
      company: '',
      phone: '',
      message: '',
      status: ''
    },
    knowledge_base: '',
    system_prompt: '',
    welcome_message: 'Ciao! Sono l\'assistente virtuale di SIOS. Come posso aiutarti a diventare partner del nostro evento?'
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/chatbot/config');
      if (res.data) {
        setConfig(prev => ({
          ...prev,
          ...res.data,
          monday_columns: res.data.monday_columns || prev.monday_columns
        }));
      }
    } catch (error) {
      console.log('No chatbot config found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/chatbot/config', config);
      toast.success('Configurazione chatbot salvata con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio della configurazione');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const updateMondayColumn = (key, value) => {
    setConfig(prev => ({
      ...prev,
      monday_columns: { ...prev.monday_columns, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Bot className="animate-pulse text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chatbot-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Chatbot Diventa Partner</h1>
          <p className="text-muted-foreground">Configura l'assistente AI per i potenziali partner</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-chatbot-config">
          <Save size={16} className="mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Configurazione'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <Card className="glass border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/20">
                <Settings2 className="text-primary" size={20} />
              </div>
              <div>
                <CardTitle>Impostazioni Generali</CardTitle>
                <CardDescription>Configura le impostazioni base del chatbot</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Chatbot Attivo</Label>
                <p className="text-xs text-muted-foreground">Abilita o disabilita il chatbot sulla pagina partner</p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => updateConfig('enabled', checked)}
                data-testid="toggle-chatbot-enabled"
              />
            </div>

            <div className="space-y-2">
              <Label>Modello AI</Label>
              <select
                value={config.ai_model}
                onChange={(e) => updateConfig('ai_model', e.target.value)}
                className="w-full p-2 rounded-lg bg-white/5 border border-white/10 text-foreground"
                data-testid="select-ai-model"
              >
                {AI_MODELS.map(model => (
                  <option key={model.value} value={model.value} className="bg-gray-900">
                    {model.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Messaggio di Benvenuto</Label>
              <textarea
                value={config.welcome_message}
                onChange={(e) => updateConfig('welcome_message', e.target.value)}
                placeholder="Messaggio iniziale del chatbot..."
                className="w-full h-24 p-3 rounded-lg bg-white/5 border border-white/10 resize-y"
                data-testid="input-welcome-message"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="glass border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Mail className="text-blue-400" size={20} />
              </div>
              <div>
                <CardTitle>Notifiche Email</CardTitle>
                <CardDescription>Configura le notifiche per i nuovi lead</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Email Notifiche</Label>
              <Input
                type="email"
                value={config.notification_email}
                onChange={(e) => updateConfig('notification_email', e.target.value)}
                placeholder="sales@example.com"
                className="bg-white/5 border-white/10"
                data-testid="input-notification-email"
              />
              <p className="text-xs text-muted-foreground">
                Riceverai una notifica a questo indirizzo quando un utente lascia i suoi contatti
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Monday.com Integration */}
        <Card className="glass border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Briefcase className="text-orange-400" size={20} />
              </div>
              <div>
                <CardTitle>Integrazione Monday.com</CardTitle>
                <CardDescription>Configura l'invio automatico dei lead su Monday</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Board ID</Label>
              <Input
                value={config.monday_board_id}
                onChange={(e) => updateConfig('monday_board_id', e.target.value)}
                placeholder="Es: 1234567890"
                className="bg-white/5 border-white/10"
                data-testid="input-monday-board-id"
              />
              <p className="text-xs text-muted-foreground">
                Trova il Board ID nell'URL di Monday: monday.com/boards/[BOARD_ID]
              </p>
            </div>

            <div className="space-y-2">
              <Label>Group ID (Opzionale)</Label>
              <Input
                value={config.monday_group_id}
                onChange={(e) => updateConfig('monday_group_id', e.target.value)}
                placeholder="Es: new_group"
                className="bg-white/5 border-white/10"
                data-testid="input-monday-group-id"
              />
            </div>

            <div className="border-t border-white/10 pt-4 mt-4">
              <Label className="text-sm font-semibold mb-3 block">Mappatura Colonne</Label>
              <p className="text-xs text-muted-foreground mb-3">
                Inserisci gli ID delle colonne della tua board Monday (trovi gli ID abilitando "Developer Mode" in Monday Labs)
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={config.monday_columns?.email || ''}
                    onChange={(e) => updateMondayColumn('email', e.target.value)}
                    placeholder="email"
                    className="bg-white/5 border-white/10 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Azienda</Label>
                  <Input
                    value={config.monday_columns?.company || ''}
                    onChange={(e) => updateMondayColumn('company', e.target.value)}
                    placeholder="company"
                    className="bg-white/5 border-white/10 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Telefono</Label>
                  <Input
                    value={config.monday_columns?.phone || ''}
                    onChange={(e) => updateMondayColumn('phone', e.target.value)}
                    placeholder="phone"
                    className="bg-white/5 border-white/10 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Messaggio/Note</Label>
                  <Input
                    value={config.monday_columns?.message || ''}
                    onChange={(e) => updateMondayColumn('message', e.target.value)}
                    placeholder="notes"
                    className="bg-white/5 border-white/10 text-sm"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Knowledge Base */}
        <Card className="glass border-white/10 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Brain className="text-green-400" size={20} />
              </div>
              <div>
                <CardTitle>Knowledge Base (RAG)</CardTitle>
                <CardDescription>Inserisci le informazioni che il chatbot utilizzerà per rispondere</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Contenuto Knowledge Base</Label>
              <textarea
                value={config.knowledge_base}
                onChange={(e) => updateConfig('knowledge_base', e.target.value)}
                placeholder={`Inserisci qui tutte le informazioni sui pacchetti di sponsorizzazione, prezzi, benefit, FAQ, ecc.

Esempio:
## Pacchetti Sponsorizzazione SIOS

### Smart Partner - €6.000
- Videointervista dedicata
- Social Plus package
- Banner in newsletter
- Logo sul sito
- Citazione nei comunicati stampa

### Spazio Espositivo - €3.500
- Desk personalizzato in area networking
- 2 pass VIP inclusi
- Visibilità materiale promozionale

## FAQ
D: Quando si svolge l'evento?
R: SIOS si svolge in diverse tappe durante l'anno: Milano, Roma, Cagliari.

D: Come posso partecipare come speaker?
R: Contatta il team commerciale per valutare opportunità di speaking slot.
`}
                className="w-full h-80 p-4 rounded-lg bg-white/5 border border-white/10 font-mono text-sm resize-y"
                data-testid="input-knowledge-base"
              />
              <p className="text-xs text-muted-foreground">
                Formatta il contenuto in modo chiaro. Il chatbot userà queste informazioni per rispondere alle domande dei potenziali partner.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Custom System Prompt */}
        <Card className="glass border-white/10 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <MessageSquare className="text-purple-400" size={20} />
              </div>
              <div>
                <CardTitle>Prompt Personalizzato (Avanzato)</CardTitle>
                <CardDescription>Aggiungi istruzioni aggiuntive per personalizzare il comportamento del chatbot</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Istruzioni Aggiuntive</Label>
              <textarea
                value={config.system_prompt}
                onChange={(e) => updateConfig('system_prompt', e.target.value)}
                placeholder="Es: Quando parli dei pacchetti, enfatizza sempre il valore del networking. Se l'utente chiede sconti, suggerisci di parlare con il team commerciale..."
                className="w-full h-32 p-4 rounded-lg bg-white/5 border border-white/10 resize-y"
                data-testid="input-system-prompt"
              />
              <p className="text-xs text-muted-foreground">
                Queste istruzioni verranno aggiunte al prompt base del chatbot per personalizzarne il comportamento.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChatbotAdminPage;
